import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { supabase } from "../lib/supabaseClient";
import { getWorkspaceConfiguration } from "../lib/salesAffiliateWorkspaceConfig";

const FUNCTION_NAME = "sales_affiliate_status";

const SalesAffiliateWorkspaceContext = createContext(null);

export function SalesAffiliateWorkspaceProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [statusLoadFailed, setStatusLoadFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [workspaceData, setWorkspaceData] = useState(null);

  const loadWorkspace = useCallback(async ({ background = false } = {}) => {
    if (!background) {
      setLoading(true);
    }
    setStatusLoadFailed(false);
    setErrorMessage("");

    try {
      const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
        body: {},
      });

      if (error) {
        throw new Error(error.message || "Unable to load Sales Affiliate status.");
      }

      if (!data?.ok) {
        throw new Error(data?.error || "Unable to load Sales Affiliate status.");
      }

      setWorkspaceData(data);
    } catch (error) {
      setStatusLoadFailed(true);
      setErrorMessage(
        error?.message || "Unable to load Sales Affiliate status."
      );
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const lifecycleState =
    workspaceData?.lifecycle_state || "REGISTERED_UNVERIFIED";

  const configuration = useMemo(
    () => getWorkspaceConfiguration(lifecycleState),
    [lifecycleState]
  );

  const value = useMemo(
    () => ({
      loading,
      statusLoadFailed,
      errorMessage,
      workspaceData,
      lifecycleState,
      configuration,
      refreshWorkspace: loadWorkspace,
    }),
    [
      loading,
      statusLoadFailed,
      errorMessage,
      workspaceData,
      lifecycleState,
      configuration,
      loadWorkspace,
    ]
  );

  return (
    <SalesAffiliateWorkspaceContext.Provider value={value}>
      {children}
    </SalesAffiliateWorkspaceContext.Provider>
  );
}

export function useSalesAffiliateWorkspace() {
  const context = useContext(SalesAffiliateWorkspaceContext);

  if (!context) {
    throw new Error(
      "useSalesAffiliateWorkspace must be used inside SalesAffiliateWorkspaceProvider"
    );
  }

  return context;
}
